import { useCallback, useEffect, useRef, useState } from 'react'
import { Box } from '@ui'
import { SECOND_MS } from 'data/constants'
import Hls from 'hls.js'

export const VideoPreview = (props: {
    posterUrl: string | undefined
    hlsSource: string | undefined
}) => {
    const { hlsSource } = props

    const MAX_VIDEO_RETRY_COUNT = 4

    const videoRef = useRef<HTMLVideoElement | null>(null)
    const hlsRef = useRef<Hls | null>(null)
    const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
    const [cacheBuster, setCacheBuster] = useState('')

    const posterUrl = props.posterUrl ? `${props.posterUrl}${cacheBuster}` : undefined

    const loadVideoWithRetry = useCallback(
        (hlsSource: string, videoElement: HTMLVideoElement, attempt = 0) => {
            if (retryTimeoutRef.current !== undefined) {
                clearTimeout(retryTimeoutRef.current)
                retryTimeoutRef.current = undefined
            }

            const separator = hlsSource.includes('?') ? '&' : '?'
            const cacheBusterParam = attempt > 0 ? `${separator}cb=${Date.now()}-${attempt}` : ''
            const urlToLoad = `${hlsSource}${cacheBusterParam}`

            // Update cache buster state for poster frame
            setCacheBuster(cacheBusterParam)

            if (Hls.isSupported()) {
                if (hlsRef.current) {
                    hlsRef.current.destroy()
                }

                const hlsInstance = new Hls({ enableWorker: true })
                hlsRef.current = hlsInstance

                // Handle HLS errors
                hlsInstance.on(Hls.Events.ERROR, (_event, data) => {
                    if (data.fatal) {
                        switch (data.type) {
                            case Hls.ErrorTypes.NETWORK_ERROR:
                                // Network error - could be "Playback ID not ready"
                                if (attempt < MAX_VIDEO_RETRY_COUNT) {
                                    const delay = SECOND_MS * 2 ** (attempt + 1) // 2s, 4s, 8s, 16s
                                    console.log(
                                        `Video failed to load (attempt ${attempt + 1}/${MAX_VIDEO_RETRY_COUNT}). Retrying in ${delay / 1000}s...`,
                                    )

                                    retryTimeoutRef.current = setTimeout(() => {
                                        loadVideoWithRetry(hlsSource, videoElement, attempt + 1)
                                    }, delay)
                                } else {
                                    console.error('Video failed to load after maximum retries')
                                }
                                break
                            case Hls.ErrorTypes.MEDIA_ERROR:
                                // Try to recover from media errors
                                hlsInstance.recoverMediaError()
                                break
                            default:
                                console.error('Fatal HLS error:', data)
                                break
                        }
                    }
                })

                hlsInstance.loadSource(urlToLoad)
                hlsInstance.attachMedia(videoElement)
            } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
                videoElement.src = urlToLoad
                videoElement.load()
            }
        },
        [setCacheBuster],
    )

    useEffect(() => {
        const videoElement = videoRef.current

        if (!videoElement) {
            return
        }

        // Cleanup previous HLS instance and timeout
        if (hlsRef.current) {
            hlsRef.current.destroy()
            hlsRef.current = null
        }

        if (retryTimeoutRef.current !== undefined) {
            clearTimeout(retryTimeoutRef.current)
            retryTimeoutRef.current = undefined
        }

        videoElement.removeAttribute('src')
        setCacheBuster('')

        if (hlsSource) {
            loadVideoWithRetry(hlsSource, videoElement, 0)
        }

        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy()
                hlsRef.current = null
            }
            if (retryTimeoutRef.current !== undefined) {
                clearTimeout(retryTimeoutRef.current)
                retryTimeoutRef.current = undefined
            }
        }
    }, [hlsSource, loadVideoWithRetry])
    return (
        <Box
            controls
            playsInline
            as="video"
            poster={posterUrl}
            autoPlay={false}
            width="100%"
            height="100%"
            objectFit="cover"
            ref={videoRef}
        />
    )
}
